import { FC } from 'react';
import { Zap } from 'lucide-react';
import { cn } from '@crisellevaldez/speculo-ui';

interface AskRemiSearchButtonProps {
  onClick: () => void;
  /** "header" — sits on the dark header surface with the ⌘K shortcut chip.
   *  "drawer" — full-width pill inside the mobile drawer (uses muted surface). */
  variant?: 'header' | 'drawer';
  /** True when running on macOS — controls the keyboard hint glyph. */
  isMac?: boolean;
  className?: string;
}

/**
 * Reusable search-button trigger for Ask Remi. The dashboard hero uses a
 * proper input bar; this component is for the header / drawer entry points
 * that just open the modal on click.
 */
export const AskRemiSearchButton: FC<AskRemiSearchButtonProps> = ({
  onClick,
  variant = 'header',
  isMac = false,
  className,
}) => {
  const isHeader = variant === 'header';

  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        'group flex w-full items-center gap-2.5 cursor-pointer rounded-lg border px-2 py-1.5 transition-colors',
        isHeader
          ? 'header-search'
          : 'bg-muted/40 dark:bg-muted/30 border-foreground/30 hover:border-amber-500/40',
        className
      )}
    >
      <div
        className={cn(
          'rounded-md p-1.5 shrink-0 transition-all',
          isHeader
            ? 'bg-white/15 group-hover:bg-gradient-to-r group-hover:from-amber-500 group-hover:to-orange-600 dark:group-hover:from-amber-300 dark:group-hover:to-orange-400'
            : 'btn-ai-gradient'
        )}
      >
        <Zap
          className={cn(
            'h-3.5 w-3.5 transition-colors',
            isHeader
              ? 'text-white dark:group-hover:text-zinc-900'
              : 'text-white dark:text-zinc-900'
          )}
        />
      </div>
      <span
        className={cn(
          'flex-1 text-left text-sm',
          isHeader ? 'header-search-input-text' : 'text-muted-foreground'
        )}
      >
        Ask Remi anything...
      </span>
      {isHeader && (
        <kbd className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-md header-kbd text-[10px] font-medium shrink-0">
          {isMac ? '⌘' : 'Ctrl'}K
        </kbd>
      )}
    </button>
  );
};

export default AskRemiSearchButton;
